import React, { useEffect } from "react";
import { Form, Row, Col, Select, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";

export default function AccountingIFRSForm({
  form,
  initialValues = {},
  onFinish,
  ifrsClassificationOptions = [],
  accrualPostingFrequencyOptions = [],
  gLMapping = [],
  accountingBasisOptions = [],
}) {
  // Prepare GL options (already {value,label})
  const glOptions = gLMapping || [];

  // Set initial values on form load
  useEffect(() => {
    if (!initialValues) return;

    // Multi-select GLs should just use the array of ids
    const normalized = {
      ...initialValues,
      principalGL: initialValues.principalGL || [],
      accruedInterestGL: initialValues.accruedInterestGL || [],
      interestIncomeGL: initialValues.interestIncomeGL || [],
      unrealisedIncomeGL: initialValues.unrealisedIncomeGL || [],
    };

    form.setFieldsValue(normalized);
  }, [initialValues, form]);

  // Handle form submission
  const handleFinish = (values) => {
    // Convert multi-select arrays back into {debitAccount, creditAccount}
    const glFields = [
      "principalGL",
      "accruedInterestGL",
      "interestIncomeGL",
      "unrealisedIncomeGL",
    ];

    const normalizedGL = {};
    glFields.forEach((field) => {
      const arr = values[field] || [];
      normalizedGL[field] = {
        debitAccount: arr[0] ? { id: arr[0] } : null,
        creditAccount: arr[1] ? { id: arr[1] } : null,
      };
    });

    onFinish({
      ...values,
      ...normalizedGL,
    });
  };

  return (
    <div>
      <h3 style={{ marginBottom: 24, fontWeight: 600 }}>
        Accounting & IFRS Behaviour
      </h3>

      <Form layout="vertical" form={form} onFinish={handleFinish}>
        {/* IFRS Classification & Accounting Basis */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={
                <span>
                  IFRS Classification
                  <Tooltip title="Determines measurement category, P&L vs OCI treatment, and impairment logic.">
                    <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
                  </Tooltip>
                </span>
              }
              name="ifrsClassification"
              rules={[{ required: true }]}
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={ifrsClassificationOptions}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={
                <span>
                  Accounting Basis
                  <Tooltip title="Selects the valuation and posting engine for accounting purposes.">
                    <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
                  </Tooltip>
                </span>
              }
              name="accountingBasis"
              rules={[{ required: true }]}
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={accountingBasisOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Revaluation Rules */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={
                <span>
                  Revaluation Required
                  <Tooltip title="Enables mark-to-market revaluation processing for the product.">
                    <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
                  </Tooltip>
                </span>
              }
              name="revaluationRequiredFlag"
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={[
                  { label: "Yes", value: true },
                  { label: "No", value: false },
                ]}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={
                <span>
                  Revaluation Frequency
                  <Tooltip title="Controls how often revaluation is executed and posted.">
                    <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
                  </Tooltip>
                </span>
              }
              name="revaluationFrequency"
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={accrualPostingFrequencyOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* GL Mappings */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={
                <span>
                  Principal GL
                  <Tooltip title="Defines where principal balances are posted on the balance sheet.">
                    <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
                  </Tooltip>
                </span>
              }
              name="principalGL"
              rules={[{ required: true }]}
            >
              <Select
                mode="multiple"
                options={glOptions}
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={
                <span>
                  Accrual GL
                  <Tooltip title="Defines where accrued income is posted during accrual runs.">
                    <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
                  </Tooltip>
                </span>
              }
              name="accruedInterestGL"
              rules={[{ required: true }]}
            >
              <Select
                mode="multiple"
                options={glOptions}
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={
                <span>
                  Interest Income GL
                  <Tooltip title="Defines where realised interest or discount income is recognised in P&L.">
                    <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
                  </Tooltip>
                </span>
              }
              name="interestIncomeGL"
              rules={[{ required: true }]}
            >
              <Select
                mode="multiple"
                options={glOptions}
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={
                <span>
                  Unrealised Income GL
                  <Tooltip title="Defines where unrealised valuation gains or losses are posted (OCI or P&L).">
                    <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
                  </Tooltip>
                </span>
              }
              name="unrealisedIncomeGL"
              rules={[{ required: true }]}
            >
              <Select
                mode="multiple"
                options={glOptions}
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Note */}
        <Row style={{ marginTop: 12 }}>
          <Col span={24}>
            <div
              style={{
                backgroundColor: "#f5f5f5",
                padding: 12,
                borderRadius: 4,
                fontSize: 12,
                color: "#666",
              }}
            >
              <strong>Note:</strong> These fields control accounting treatment,
              valuation, accrual posting, and financial reporting behaviour.
            </div>
          </Col>
        </Row>

        <button type="submit" hidden />
      </Form>
    </div>
  );
}
